import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges,
  OnDestroy, Output, SimpleChanges, ViewChild,
} from '@angular/core';
import {GridStack, GridStackWidget} from 'gridstack';
import {DashboardLayout} from '../../interfaces/metrics';

// Thin gridstack.js wrapper (mode A: gridstack.init() over Angular-rendered
// `.grid-stack-item` DOM projected via <ng-content>). The dashboard owns the widget
// markup + the per-item gs-* position attrs; this component owns init, the
// static/arrange toggle, and serializing drag/resize back out as a DashboardLayout.
//
// Mode A wraps the existing DOM in place (no reparent), so the dashboard's own
// @ViewChild widget-host refs (retry()) keep resolving.
@Component({
    selector: 'bst-dashboard-grid',
    templateUrl: './dashboard-grid.component.html',
    styleUrls: ['./dashboard-grid.component.scss'],
    standalone: false
})
export class DashboardGridComponent implements AfterViewInit, OnChanges, OnDestroy {

  // Draggable + resizable when true; frozen (display) when false.
  @Input() arranging = false;
  // Applied coords. Used to re-sync gridstack when the parent swaps the layout OUT of
  // arrange mode (Save/Reset); during arranging gridstack is the source of truth.
  @Input() layout: DashboardLayout = {};
  // Stable signature of the rendered widget set (a string, so an unchanged set is
  // Object.is-equal and does not re-fire ngOnChanges). Changing it (admin show/hide,
  // or the async layout arriving after first paint) triggers a resync so gridstack
  // never manages a stale item set.
  @Input() idsKey = '';
  // Counter the parent bumps to force a reload (e.g. Reset layout) even mid-edit, when
  // the normal layout-change resync is intentionally gated off.
  @Input() resetTick = 0;
  // Grid row height in px. sizeToContent rounds each tile UP to a whole number of these
  // rows, so a large value leaves dead space under short tiles (KPI cards). Both the
  // Self and Company tabs now pass 40 so their KPI tiles hug their content instead of
  // snapping to fat bands; 80 remains only as the fallback default. Read once at init.
  @Input() cellHeight = 80;

  // Emitted on every drag/resize with the full current arrangement.
  @Output() layoutChange = new EventEmitter<DashboardLayout>();

  @ViewChild('gridEl') private gridEl!: ElementRef<HTMLElement>;

  private grid?: GridStack;
  private reinitScheduled = false;
  private pendingReinit = false;
  private contentObserver?: ResizeObserver;
  private flushTimer?: ReturnType<typeof setTimeout>;
  private flushAttempts = 0;
  // 200 * 150ms = 30s: long enough to catch a viewport widening back out shortly after
  // the change that deferred the reinit, short enough that a viewport settled on mobile
  // does not poll forever. Giving up here is safe: the next genuine reinit trigger
  // (Save/Reset/idsKey/arranging change) calls scheduleFlushCheck() again from scratch.
  private static readonly MAX_FLUSH_ATTEMPTS = 200;
  // Incremented on 'resizestop', decremented on a matching 'resizecontent' (see initGrid):
  // the emit must happen after gridstack's own post-resize content-fit settles, not before.
  // A COUNTER, not a boolean: 'resizecontent' fires once per deferred node check, so two
  // overlapping resizes each need their own slot. A boolean let the first resize's settle
  // consume the only flag, so the second resize's settle found it already false and never
  // emitted, silently dropping its content-fit push.
  private pendingResizeEmit = 0;
  // Per-tile min column span, captured at the FIRST init. gridstack consumes the
  // gs-min-w attr Angular rendered and strips it from the DOM, and Angular only rewrites
  // an [attr.*] binding when its bound value changes, so every later reinit would lose
  // the floor (live repro: a resize crushed the min-w=4 assignments table to w=1).
  private minWById = new Map<string, number>();

  ngAfterViewInit(): void {
    // Keyboard arrange path: one delegated listener on the persistent grid element (it
    // survives every gridstack reinit). Grips are real <button>s, so keyboard users can
    // Tab to a tile's grip and arrow-move it at parity with mouse drag.
    this.gridEl.nativeElement.addEventListener('keydown', this.onGripKeydown);
    this.initGrid();
  }

  // Flush a reinit that was deferred while the grid was responsive-collapsed (see
  // reinit()). Polling is deliberate: gridstack handles its own resize through a
  // throttle (~100ms), so a one-shot signal (RO + one rAF) can check before
  // getColumn() reflects the grow-back and then never fire again, stranding the
  // deferred resync. The poll only runs while a reinit is actually pending (a rare,
  // short-lived state) and stops as soon as it flushes or the component dies.
  private scheduleFlushCheck(): void {
    if (this.flushTimer !== undefined) { return; }
    this.flushAttempts = 0;
    const check = () => {
      this.flushTimer = undefined;
      if (!this.pendingReinit || !this.grid) { return; }
      if (this.grid.getColumn() === 12) {
        this.pendingReinit = false;
        this.scheduleReinit();
        return;
      }
      // Bounded: see MAX_FLUSH_ATTEMPTS above for why giving up here is safe.
      if (++this.flushAttempts >= DashboardGridComponent.MAX_FLUSH_ATTEMPTS) {
        this.pendingReinit = false;
        return;
      }
      this.flushTimer = setTimeout(check, 150);
    };
    this.flushTimer = setTimeout(check, 150);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.grid) { return; }                              // pre-init input sets
    // Entering/leaving edit: reinit so gridstack (re)binds drag against the
    // .bst-drag-handle grips that only render in edit mode, and applies the
    // static/draggable state. setStatic alone would not pick up the handles.
    if (changes['arranging']) {
      this.scheduleReinit();
      return;
    }
    // Explicit reset: reload even mid-edit (the resync below is gated off while editing).
    if (changes['resetTick'] && !changes['resetTick'].firstChange) {
      this.scheduleReinit();
      return;
    }
    // A layout swap (Save/Reset) or a rendered-set change (admin show/hide, or the
    // async layout arriving after first paint) while NOT arranging must resync the
    // grid to the current DOM. Ignored during arranging (gridstack owns positions).
    const resync = (changes['layout'] && !changes['layout'].firstChange)
                || (changes['idsKey'] && !changes['idsKey'].firstChange);
    if (resync && !this.arranging) {
      this.scheduleReinit();
    }
  }

  ngOnDestroy(): void {
    this.gridEl?.nativeElement.removeEventListener('keydown', this.onGripKeydown);
    this.contentObserver?.disconnect();
    if (this.flushTimer !== undefined) { clearTimeout(this.flushTimer); this.flushTimer = undefined; }
    this.pendingReinit = false;
    this.pendingResizeEmit = 0;
    this.grid?.destroy(false);
    this.grid = undefined;
  }

  // Arrow keys on a focused grip move the tile one cell (Shift+Left/Right changes its
  // width), emitting the SAME layoutChange a mouse drag does, so the parents'
  // draft/Save/Cancel/Reset semantics apply unchanged. Guards mirror the drag path:
  // edit mode only (grips are only focusable there) and 12-column only (1-col coords
  // must never reach a draft). Clamps mirror the engine constraints: x+w<=12, w>=minW.
  // There is deliberately NO keyboard height resize: sizeToContent auto-manages h and
  // snaps any manual h back to the content fit (true for mouse vertical resize too), so
  // offering it would be a false affordance (review finding).
  private onGripKeydown = (ev: KeyboardEvent): void => {
    if (!ev.key.startsWith('Arrow')) { return; }
    const grip = (ev.target as HTMLElement).closest('.bst-drag-handle');
    if (!grip || !this.grid || !this.arranging || this.grid.getColumn() !== 12) { return; }
    const item = grip.closest('.grid-stack-item') as HTMLElement | null;
    const node = this.grid.engine.nodes.find(n => n.el === item);
    if (!item || !node) { return; }
    const x = node.x ?? 0, y = node.y ?? 0, w = node.w ?? 1;
    const patch: GridStackWidget = {};
    if (ev.shiftKey) {
      if (ev.key === 'ArrowRight') { patch.w = Math.min(12 - x, w + 1); }
      if (ev.key === 'ArrowLeft')  { patch.w = Math.max(node.minW ?? 1, w - 1); }
    } else {
      if (ev.key === 'ArrowRight') { patch.x = Math.min(12 - w, x + 1); }
      if (ev.key === 'ArrowLeft')  { patch.x = Math.max(0, x - 1); }
      if (ev.key === 'ArrowDown')  { patch.y = y + 1; }
      if (ev.key === 'ArrowUp')    { patch.y = Math.max(0, y - 1); }
    }
    // Drop no-ops BEFORE preventDefault: an unhandled combination (e.g. Shift+Up) or a
    // fully-clamped move keeps its native behavior and emits no identical-draft churn.
    const changed = (patch.x !== undefined && patch.x !== x)
                 || (patch.y !== undefined && patch.y !== y)
                 || (patch.w !== undefined && patch.w !== w);
    if (!changed) { return; }
    ev.preventDefault();
    ev.stopPropagation();
    this.grid.update(item, patch);
    this.layoutChange.emit(this.currentLayout());
  };

  private initGrid(): void {
    this.grid = GridStack.init(
      {
        column: 12, cellHeight: this.cellHeight, margin: 8, staticGrid: !this.arranging,
        // Size each tile's height to its content so empty tiles shrink and tall tables
        // get room instead of scrolling inside a fixed cell; float:false compacts
        // vertically so tiles never leave gaps. gridstack only measures content at
        // layout events (init, column change, add/remove), NOT when content changes
        // size afterwards, so observeContent() below re-fits on async widget loads.
        sizeToContent: true, float: false,
        // Below 768px of GRID width (so the member-drill drawer collapses too) render a
        // single stacked column in saved y-order. This is PRESENTATION ONLY: gridstack
        // caches the 12-col coords when leaving 12 columns and restores them exactly on
        // grow-back (verified live: byte-identical restore, zero drag/resize emits), and
        // the emit guard below plus the hidden arrange toolbars keep any 1-col state out
        // of the draft, so a breakpoint change can never write to the stored layout.
        columnOpts: { breakpoints: [{ w: 768, c: 1, layout: 'list' }] },
        // Drag only by the explicit grip so a drag never starts from the show/hide
        // checkbox, a param input, or while scrolling a tile's content.
        handle: '.bst-drag-handle',
        // East handle only. Tile height is content-derived (sizeToContent above) and the PUT
        // endpoint stores x/y/w only, so a height the user drags is recomputed from content
        // and never persisted; gridstack's default 'se' handle drags both, advertising a
        // dimension the system discards. West is dropped too: it overlaps the drag grip on
        // the tile's left edge and would steal clicks from it.
        resizable: { handles: 'e' },
      },
      this.gridEl.nativeElement,
    );
    for (const n of this.grid.engine.nodes) {
      if (typeof n.id === 'string' && n.minW != null && !this.minWById.has(n.id)) {
        this.minWById.set(n.id, n.minW);
      }
    }
    this.applyAuthoritativeState();
    // Emit only on user drag/resize completion, never on gridstack's own sizeToContent
    // height moves, so auto-sizing can't churn or persist an h the user did not choose.
    // (dragstop/resizestop only fire while draggable, i.e. in edit mode.) The 12-column
    // guard makes arrange desktop-only at the data level: if the viewport crosses the
    // mobile breakpoint MID-edit, a drag in the collapsed grid would otherwise serialize
    // 1-col normalized coords into the draft and a later Save would overwrite the user's
    // real 12-col layout with a flattened one (the corruption case the plan forbids).
    this.grid.on('dragstop', () => {
      if (this.grid?.getColumn() !== 12) { return; }
      this.layoutChange.emit(this.currentLayout());
    });
    // resizestop ALSO emits directly: gridstack.js's onEndMoving fires 'resizestop' BEFORE
    // it calls resizeToContentCheck() (see gridstack.js ~2645), which re-fits the resized
    // tile to its content height and, under float:false, can push or compact neighbouring
    // rows as a result; that check defers via setTimeout(..., animationDelay), ~310ms with
    // the default animation CSS. Emitting only on the later 'resizecontent' event left that
    // whole window where a Save read a draft still holding the pre-resize arrangement,
    // silently dropping the resize. Emitting here too closes that window; the settled
    // 'resizecontent' emit below still overwrites this snapshot once content-sizing has
    // run, so the draft never sticks at a transient pre-settle shape either (last write
    // wins either way).
    this.grid.on('resizestop', () => {
      if (this.grid?.getColumn() !== 12) { return; }
      this.pendingResizeEmit++;
      this.layoutChange.emit(this.currentLayout());
    });
    this.grid.on('resizecontent', () => {
      if (this.pendingResizeEmit <= 0) { return; }   // not the tail of a user resize, ignore
      // Column guard BEFORE consuming a slot: if the viewport collapses during the
      // settle window, the count must survive so the eventual settle (once back at 12
      // columns) still emits, instead of this resize being silently dropped for good.
      if (this.grid?.getColumn() !== 12) { return; }
      this.pendingResizeEmit--;
      this.layoutChange.emit(this.currentLayout());
    });
    this.observeContent();
  }

  // A (re)init CANNOT trust the DOM's gs-* attrs: gridstack rewrites gs-x/y/w/h while
  // dragging and strips constraint attrs at parse, and Angular only rewrites an
  // [attr.gs-*] binding when its bound value changes, so after any drag the DOM no
  // longer reflects [layout] (live repro: Cancel and Reset left tiles rendered at the
  // abandoned drag position until reload). Program the engine from the [layout] input
  // (the single source of truth) and the cached min-w floors instead. Tiles without a
  // [layout] entry (e.g. the never-fetching drill view) keep their attr-derived spot.
  private applyAuthoritativeState(): void {
    if (!this.grid) { return; }
    // [layout] coords are 12-column coords. If a reinit happens while the responsive
    // breakpoint has the grid collapsed, writing them into the 1-col engine would both
    // wreck the stacked view and poison the cached 12-col layout gridstack restores on
    // grow-back. Only the min-w floors are safe (and needed) at any column count.
    const at12 = this.grid.getColumn() === 12;
    this.grid.batchUpdate();
    // min-w floor first: order-independent (it never moves a node), so it can run over
    // engine.nodes in whatever order they come in.
    for (const n of this.grid.engine.nodes) {
      if (typeof n.id !== 'string' || !n.el) { continue; }
      const minW = this.minWById.get(n.id);
      if (minW != null) { this.grid.update(n.el, { minW }); }
    }
    // x/y/w only, and only at12 (see above). `h` is never written back: sizeToContent has
    // already grown every tile to its content height by the time this runs, so the stored
    // h (from the old fixed-height layout) contradicts the real height. Writing it back
    // made every row collide with the taller tile above it, cascading the whole grid and
    // landing the LAST row on top (measured live, see class header). Height is content-
    // owned from here on; currentLayout() below matches by no longer serializing it.
    //
    // Nodes are repositioned in ascending (y, x) order of the INTENDED layout, not
    // engine.nodes order, and stored y is treated as a row RANK rather than an absolute row
    // (a gap sized for the old fixed h means nothing once height is content-derived). But
    // grid.update() is not a passive position write: on a live collision it runs gridstack's
    // own push-down resolution (GridStackEngine._fixCollisions), using the MOVING node's
    // still-tall content height as the push distance. Consecutive ranks (0, 1, 2, ...)
    // guarantee exactly that collision against whatever still occupies the row above (its
    // real height hasn't shrunk to the rank spacing yet), and each push cascades into
    // whatever sits below THAT too - reproducing the very cascade/inversion this fix removes,
    // just from a different trigger (verified live: consecutive ranks left the last group on
    // top again). Space ranks far enough apart that a group can never collide with the one
    // above it - the sum of every node's current (still content-tall) height is a safe upper
    // bound for any single one - so every update() below is a plain, collision-free write.
    // float: false compaction (batchUpdate(false) below) then pulls each group up to sit
    // directly under the real bottom of the one above it, closing the artificial gap.
    if (at12) {
      const targets: { el: HTMLElement; cell: DashboardLayout[string] }[] = [];
      for (const n of this.grid.engine.nodes) {
        if (typeof n.id !== 'string' || !n.el) { continue; }
        const cell = this.layout[n.id];
        if (cell) { targets.push({ el: n.el, cell }); }
      }
      targets.sort((a, b) => (a.cell.y - b.cell.y) || (a.cell.x - b.cell.x));
      const rankGap = 1 + this.grid.engine.nodes.reduce((sum, n) => sum + (n.h ?? 0), 0);
      let rank = -1;
      let lastY: number | undefined;
      for (const { el, cell } of targets) {
        if (cell.y !== lastY) { rank++; lastY = cell.y; }
        this.grid.update(el, { x: cell.x, y: rank * rankGap, w: cell.w });
      }
    }
    this.grid.batchUpdate(false);
  }

  // gridstack's single ResizeObserver watches the grid element, not each tile's content,
  // so a widget whose data arrives after layout would keep its initial (empty) height.
  // Observe each tile's content box and re-fit the tile when it grows/shrinks. The
  // observed content is natural-height, so resizeToContent (which sets the ITEM height)
  // does not feed back into it, so no loop.
  private observeContent(): void {
    this.contentObserver?.disconnect();
    if (typeof ResizeObserver === 'undefined') { return; }
    this.contentObserver = new ResizeObserver(entries => {
      for (const e of entries) {
        const item = (e.target as HTMLElement).closest('.grid-stack-item');
        if (item) { this.grid?.resizeToContent(item as HTMLElement); }
      }
    });
    this.gridEl.nativeElement
      .querySelectorAll('.grid-stack-item > .grid-stack-item-content > *')
      .forEach(child => this.contentObserver!.observe(child));
  }

  // Defer the resync to a microtask so Angular finishes writing the new gs-* attrs and
  // item set (both bound by the parent, later in the same change-detection tick) before
  // gridstack re-reads them; a synchronous reinit here would init over stale DOM.
  private scheduleReinit(): void {
    if (this.reinitScheduled) { return; }
    this.reinitScheduled = true;
    queueMicrotask(() => {
      this.reinitScheduled = false;
      // Guard against a pending microtask firing after teardown. The arranging vs
      // not-arranging gating already happened at schedule time in ngOnChanges.
      if (this.grid) { this.reinit(); }
    });
  }

  // Tear down + re-init over the current DOM. destroy(false) keeps the .grid-stack-item
  // nodes so Angular's view is untouched.
  private reinit(): void {
    // Never rebuild gridstack over responsive-collapsed DOM: a destroy+init at 1 column
    // parses the collapsed gs-* attrs as the true layout and seeds the NEW instance's
    // 12-column cache with a flattened stack, which grow-back would restore and a later
    // desktop drag would emit and persist (final-review finding; the hard invariant is
    // that 1-col coords never reach the stored layout). Defer instead: the host width
    // observer flushes the reinit as soon as the grid is back at 12 columns. Editing is
    // impossible while collapsed (toolbars hidden, emits guarded), so the deferred
    // resync cannot surface as user-visible staleness that matters.
    if (this.grid && this.grid.getColumn() !== 12) {
      this.pendingReinit = true;
      this.scheduleFlushCheck();
      return;
    }
    // This rebuild also satisfies any reinit deferred while collapsed: consume the
    // pending state so the flush poller cannot fire a redundant second destroy/init
    // right after this one (single-flush invariant).
    this.pendingReinit = false;
    if (this.flushTimer !== undefined) { clearTimeout(this.flushTimer); this.flushTimer = undefined; }
    // A resize awaiting its settle cannot outlive the grid instance it was raised
    // against: the rebuilt grid's own 'resizecontent' events must start counting fresh,
    // never consume a stale slot left over from before the edit session that triggered
    // this reinit (which would emit a layoutChange the parent never asked for).
    this.pendingResizeEmit = 0;
    this.grid?.destroy(false);
    this.initGrid();
  }

  // Serialize the live grid into a DashboardLayout keyed by each item's gs-id. `h` is never
  // included: it is content-derived (sizeToContent), so persisting it would hand the next
  // reinit a stale height that contradicts whatever the content measures to next time (see
  // applyAuthoritativeState).
  private currentLayout(): DashboardLayout {
    const out: DashboardLayout = {};
    const saved = (this.grid?.save(false) as GridStackWidget[] | undefined) ?? [];
    for (const w of saved) {
      const id = w.id;
      if (typeof id === 'string' && w.x != null && w.y != null && w.w != null) {
        out[id] = { x: w.x, y: w.y, w: w.w };
      } else {
        // A grid node without a stable gs-id/coords would silently vanish from every
        // save; surface it in dev so a future widget added without gs-id is caught.
        console.warn('dashboard-grid: dropping unserializable node from layout', w);
      }
    }
    return out;
  }
}
